import { gql, MutationResult, useMutation } from '@apollo/client';
import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { useCallback } from 'react';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { CustomerEngagementPlanning, Tactic } from 'generated/maya.types';
import { cepFragment } from 'modules/maya/graphql/fragments/cep.fragments';
import { tacticFragment } from 'modules/maya/graphql/fragments/tactic.fragments';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const duplicateCepMutationCall = gql`
mutation duplicateCustomerEngagementPlanning(
    $sourceCustomerEngagementPlanningId: String!,
    $duplicateCustomerEngagementPlanning: CustomerEngagementPlanningInput!,
    $sendToVeeva: Boolean
    ) {
        duplicateCustomerEngagementPlanning(
            sourceCustomerEngagementPlanningId: $sourceCustomerEngagementPlanningId,
            duplicateCustomerEngagementPlanningInput: $duplicateCustomerEngagementPlanning,
            sendToVeeva: $sendToVeeva
        ) {
    cep {
      ${cepFragment}
    }
    tactics {
      ${tacticFragment}
    }
  }
}
`;

type ResultType = {
  duplicateCustomerEngagementPlanning: {
    cep: CustomerEngagementPlanning;
    tactics: Tactic[];
  };
};

type UseDuplicateCepMutation = [
  (
    sourceCustomerEngagementPlanningId: string,
    duplicateCustomerEngagementPlanning: Partial<CustomerEngagementPlanning>,
    sendToVeeva?: boolean
  )
  => Promise<{ cep: CustomerEngagementPlanning; tactics: Tactic[] } | undefined | null>, MutationResult<ResultType>,
];

export const useDuplicateCepMutation = (): UseDuplicateCepMutation => {
  const [duplicateCep, state] = useMutation<ResultType>(duplicateCepMutationCall, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });

  const flags = useFlags();

  const executeMutation = useCallback(async (sourceId, customerEngagementPlanning, sendToVeeva = true) => {
    try {
      const { data } = await duplicateCep({
        variables: {
          sourceCustomerEngagementPlanningId: sourceId,
          duplicateCustomerEngagementPlanning: customerEngagementPlanning,
          sendToVeeva: flags.mayaEnableVeevaBiogenlinc1816 && sendToVeeva,
        },
      });
      return data?.duplicateCustomerEngagementPlanning;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, customerEngagementPlanning);
        Logger.logError({
          error,
          customContext,
          transactionName: 'duplicateCustomerEngagementPlanning.ts - useDuplicateCustomerEngagementPlanningMutation',
        });
      }
      return null;
    }
  }, [duplicateCep, flags.mayaEnableVeevaBiogenlinc1816]);

  return [executeMutation, state];
};
